import React from "react";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import twMerge from "@v1/components/common/designSystem/utils/twMerge";
import { X } from "lucide-react";
import { useLocation } from "react-router-dom";

import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "../shadcn/core/dialog";

/** @deprecated Used only by legacy Popover for z-index layering. Will be removed when Popover is migrated to v2. */
export const ModalContext = React.createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
} | null>(null);

type BaseModalProps = {
  onOpenChange: (open: boolean) => void;
  isOpen: boolean;
  layoutClassName?: string;
  UNSAFE_className?: string;
  overlayClassName?: string;
  closeOnOverlayClick?: boolean;
  isClosable?: boolean;
  animateScale?: boolean;
};

type DefaultModalProps = BaseModalProps & {
  variant?: "default";
  title?: string;
  titleAlignment?: "left" | "center";
  description?: string;
  footer?: React.ReactNode;
  size?: "sm" | "base" | "lg" | "custom";
};

type SplitModalProps = BaseModalProps & {
  variant: "split";
  imageSrc: string;
  header: React.ReactNode;
};

type ModalProps = DefaultModalProps | SplitModalProps;

/** @deprecated Use Modal from @replohq/lattice/components/modal/Modal instead */
const Modal: React.FC<React.PropsWithChildren<ModalProps>> = (props) => {
  const {
    children,
    onOpenChange,
    isOpen,
    layoutClassName,
    UNSAFE_className,
    overlayClassName,
    closeOnOverlayClick = true,
    isClosable = true,
    animateScale = true,
  } = props;

  // Only the agent surface is themeable (and can go dark); every other screen
  // keeps a plain white modal that ignores theme / dark-mode tokens.
  const { pathname } = useLocation();
  const isAgentRoute = pathname.startsWith("/agent");

  if (props.variant === "split") {
    return (
      <ModalContext.Provider value={{ open: isOpen, setOpen: onOpenChange }}>
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
          <DialogOverlay
            className={twMerge("bg-[#1E293B]/50", overlayClassName)}
          />
          <DialogPrimitive.Content
            className={twMerge(
              "bg-background fixed left-[50%] top-[50%] z-50 flex translate-x-[-50%] translate-y-[-50%] flex-row overflow-hidden rounded-lg shadow-lg",
              animateScale
                ? "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-75 data-[state=open]:zoom-in-75 duration-200"
                : "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-150",
              "shadow-lg h-[448px] w-[775px] max-h-[90vh] max-w-[90vw] bg-white",
              layoutClassName,
              UNSAFE_className,
            )}
            onInteractOutside={(e) => {
              if (!closeOnOverlayClick) {
                e.preventDefault();
              }
            }}
            onOpenAutoFocus={(e) => {
              e.preventDefault();
            }}
          >
            <div className="flex h-full w-[375px] shrink-0 items-center justify-center bg-black">
              <img
                src={props.imageSrc}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex w-[400px] shrink-0 flex-col bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                {props.header}
                {isClosable && (
                  <DialogClose className="focus:ring-primary data-[state=open]:bg-primary data-[state=open]:text-muted-foreground rounded-xs focus:outline-hidden ml-4 opacity-70 ring-offset-transparent transition-opacity hover:opacity-100 focus:ring-1 focus:ring-offset-1 disabled:pointer-events-none">
                    <X size={24} />
                    <span className="sr-only">Close</span>
                  </DialogClose>
                )}
              </div>
              {children}
            </div>
          </DialogPrimitive.Content>
        </Dialog>
      </ModalContext.Provider>
    );
  }

  const {
    title,
    description,
    footer,
    size = "base",
    titleAlignment = "left",
  } = props;

  return (
    <ModalContext.Provider value={{ open: isOpen, setOpen: onOpenChange }}>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        {/* TODO (Craig, 04/30/2025): Update this color after color changes are made when tailwind updates are fixed */}
        <DialogOverlay
          className={twMerge("bg-[#1E293B]/50", overlayClassName)}
        />
        <DialogPrimitive.Content
          className={twMerge(
            "border-0.5 border-border fixed left-[50%] top-[50%] z-50 flex w-full translate-x-[-50%] translate-y-[-50%] flex-col gap-4 rounded-lg p-6 shadow-lg",
            animateScale
              ? "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-75 data-[state=open]:zoom-in-75 duration-200"
              : "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-150",
            "shadow-lg w-max-[unset] max-h-[90vh] max-w-[90vw] overflow-y-auto",
            isAgentRoute ? "bg-sheet" : "bg-white",
            size === "sm" && "w-96",
            size === "base" && "w-[576px]",
            size === "lg" && "w-[768px]",
            size === "custom" && "w-fit",
            layoutClassName,
            UNSAFE_className,
          )}
          onInteractOutside={(e) => {
            if (!closeOnOverlayClick) {
              e.preventDefault();
            }
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
        >
          {(title || description || isClosable) && (
            <DialogHeader>
              <div className="flex flex-row items-start gap-2">
                {title && (
                  <DialogTitle
                    className={twMerge(
                      "header-h4 text-default flex-1",
                      titleAlignment === "center" && "text-center",
                    )}
                  >
                    {title}
                  </DialogTitle>
                )}
                {isClosable && (
                  <DialogClose className="focus:ring-primary data-[state=open]:bg-primary data-[state=open]:text-muted-foreground rounded-xs focus:outline-hidden opacity-70 ring-offset-transparent transition-opacity hover:opacity-100 focus:ring-1 focus:ring-offset-1 disabled:pointer-events-none">
                    <X size={24} />
                    <span className="sr-only">Close</span>
                  </DialogClose>
                )}
              </div>
              {description && (
                <DialogDescription
                  className={twMerge(
                    "text-muted !mt-0 text-sm",
                    titleAlignment === "center" && "text-center",
                  )}
                >
                  {description}
                </DialogDescription>
              )}
            </DialogHeader>
          )}
          {children}
          {footer && <DialogFooter>{footer}</DialogFooter>}
        </DialogPrimitive.Content>
      </Dialog>
    </ModalContext.Provider>
  );
};

Modal.displayName = "Modal";

export { Modal };
